/**
 * Maps a Mycelium event onto the Orchestration Console row shape.
 *
 * - `summarize`: builds a friendly one-liner for the timeline.
 * - `inferStatus`: derives `started | succeeded | failed | info` from the
 *   event type so the UI can show the right icon without a per-domain switch.
 * - `CONSOLE_DOMAIN_ALLOWLIST`: domains whose events are mirrored to the log
 *   table. Anything outside this list is dropped at the tap to keep noise out
 *   of the user-facing console (heartbeats, ticks, internal book-keeping).
 *
 * Callers must keep this module side-effect free — it ships into the brain
 * edge function (Deno) and the in-app backbone tap (browser/Node).
 */

import type {
  CortexEventLogStatus,
  CortexEventLogSource,
} from "@flowstate/types";

export const CONSOLE_DOMAIN_ALLOWLIST = new Set<string>([
  "ai",
  "cortex",
  "task",
  "notification",
  "gamification",
  "accounting",
  "webhook",
  "system",
  "memory",
  "box",
  "notebook",
]);

export interface SummarizeEventInput {
  domain: string;
  type: string;
  payload?: Record<string, unknown> | null;
  durationMs?: number | null;
  source?: CortexEventLogSource;
}

const titleCase = (value: string): string =>
  value
    .split(/[._-]/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const formatDuration = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "";
  if (ms < 1_000) return ` in ${Math.round(ms)}ms`;
  return ` in ${(ms / 1_000).toFixed(2)}s`;
};

const pickString = (
  payload: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined => {
  if (!payload) return undefined;
  const value = payload[key];
  return typeof value === "string" ? value : undefined;
};

/**
 * Build a friendly one-line summary for the Console timeline.
 *
 * Domain-specific shortcuts cover the highest-volume event types; everything
 * else falls back to a deterministic title-cased rendering of `domain.type`.
 */
export function summarizeEvent(input: SummarizeEventInput): string {
  const { domain, type, payload } = input;
  const duration = formatDuration(input.durationMs);

  if (domain === "ai") {
    if (type.startsWith("specialist.")) {
      const specialist = pickString(payload, "specialist") ?? "specialist";
      if (type.endsWith(".completed")) return `Specialist ${specialist} completed${duration}`;
      if (type.endsWith(".failed")) {
        const reason = pickString(payload, "error") ?? "unknown error";
        return `Specialist ${specialist} failed: ${reason}`;
      }
      if (type.endsWith(".started")) return `Specialist ${specialist} started`;
      if (type.endsWith(".parallel_completed")) {
        const count = (payload?.["count"] as number | undefined) ?? 0;
        return `Parallel specialists completed (${count})${duration}`;
      }
    }
    if (type.startsWith("graph.node.")) {
      const node = pickString(payload, "node") ?? "node";
      if (type.endsWith(".transition")) {
        const from = pickString(payload, "fromNode") ?? "?";
        const to = pickString(payload, "toNode") ?? "?";
        return `Graph ${from} → ${to}`;
      }
      return `Graph node ${node}${duration}`;
    }
  }

  if (domain === "cortex") {
    if (type === "session.started") {
      const model = pickString(payload, "model") ?? "model";
      return `Cortex session started (${model})`;
    }
    if (type === "session.completed") {
      const tokens = payload?.["tokens"] as number | undefined;
      return `Cortex session completed${duration}${tokens ? ` · ${tokens} tokens` : ""}`;
    }
    if (type === "context_enriched" || type === "user_context_enriched") {
      return "Cortex context enriched";
    }
  }

  if (domain === "task") {
    const taskTitle = pickString(payload, "title") ?? pickString(payload, "task_title");
    if (type.endsWith(".created")) return `Task created${taskTitle ? `: ${taskTitle}` : ""}`;
    if (type.endsWith(".completed")) return `Task completed${taskTitle ? `: ${taskTitle}` : ""}`;
    if (type.endsWith(".updated")) return `Task updated${taskTitle ? `: ${taskTitle}` : ""}`;
  }

  if (domain === "notification") {
    const channel = pickString(payload, "channel") ?? "in-app";
    if (type.endsWith(".sent")) return `Notification sent via ${channel}`;
    if (type.endsWith(".delivered")) return `Notification delivered (${channel})`;
    if (type.endsWith(".failed")) return `Notification failed on ${channel}`;
  }

  if (domain === "webhook") {
    const target = pickString(payload, "target") ?? pickString(payload, "url") ?? "endpoint";
    if (type.endsWith(".received")) return `Webhook received from ${target}`;
    if (type.endsWith(".dispatched")) return `Webhook dispatched to ${target}`;
  }

  if (domain === "gamification" && type.endsWith(".awarded")) {
    const points = payload?.["points"] as number | undefined;
    return `XP awarded${points ? ` (+${points})` : ""}`;
  }

  if (domain === "memory" && type === "consolidate_requested") {
    return "Memory consolidation requested";
  }

  // Fallback: title-case the namespace
  return `${titleCase(domain)} · ${titleCase(type)}${duration}`;
}

/**
 * Map an event type onto the Console status enum.
 * Unknown types default to `info` so producers don't have to plumb status.
 */
export function inferStatus(type: string): CortexEventLogStatus {
  if (type.endsWith(".failed") || type.endsWith(".error")) return "failed";
  if (type.endsWith(".completed") || type.endsWith(".succeeded") || type.endsWith(".sent") || type.endsWith(".delivered")) {
    return "succeeded";
  }
  if (type.endsWith(".started") || type.endsWith(".received") || type.endsWith(".dispatched")) {
    return "started";
  }
  return "info";
}
